import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Form.css';
import { furiganaToFullWidth, toFullWidthKatakana } from '../lib/kanaNormalize';
import { requiredFieldClass } from '../lib/requiredFieldClass';
import { applicationSuccessMessage } from '../lib/applicationSuccessMessage';
import { searchBanks, searchBranches } from '../lib/bankSearch';

const DRAFT_KEY = 'draft_bank_change';

const BankChangeForm = ({ onBack, onNext, isFirstForm, isLastForm, isMultiForm, id, isDraft }) => {
    const [formData, setFormData] = React.useState({
        bankName: '',
        branchName: '',
        branchNameKana: '',
        accountNumber: '',
        desiredDate: '',
    });
    const [bankQuery, setBankQuery] = useState('');
    const [bankOptions, setBankOptions] = useState([]);
    const [selectedBankCode, setSelectedBankCode] = useState('');
    const [branchQuery, setBranchQuery] = useState('');
    const [branchOptions, setBranchOptions] = useState([]);

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        try {
            const saved = localStorage.getItem(DRAFT_KEY);
            if (saved) setFormData((prev) => ({ ...prev, ...JSON.parse(saved) }));
        } catch (_) {}
    }, []);

    const fetchBanks = useCallback(async (q) => {
        setBankOptions(await searchBanks(q));
    }, []);

    const fetchBranches = useCallback(async (bankCode, q) => {
        setBranchOptions(await searchBranches(bankCode, q));
    }, []);

    useEffect(() => {
        const id = setTimeout(() => fetchBanks(bankQuery), 320);
        return () => clearTimeout(id);
    }, [bankQuery, fetchBanks]);

    useEffect(() => {
        const id = setTimeout(() => fetchBranches(selectedBankCode, branchQuery), 320);
        return () => clearTimeout(id);
    }, [selectedBankCode, branchQuery, fetchBranches]);

    const selectBank = (b) => {
        const disp = b.normalize?.name || b.name || '';
        setFormData((prev) => ({
            ...prev,
            bankName: disp,
            branchName: '',
            branchNameKana: '',
        }));
        setSelectedBankCode(b.code || '');
        setBankOptions([]);
        setBranchQuery('');
        setBranchOptions([]);
    };

    const selectBranch = (br) => {
        const name = br.normalize?.name || br.name || '';
        const kana =
            br.normalize?.kana ||
            br.kana ||
            '';
        setFormData((prev) => ({
            ...prev,
            branchName: name,
            branchNameKana: toFullWidthKatakana(furiganaToFullWidth(kana)),
        }));
        setBranchOptions([]);
    };

    const handleSaveDraft = () => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
        alert('一時保存しました。');
    };

    const handleDeleteDraft = async () => {
        const confirmDelete = window.confirm("この下書き（一時保存データ）を削除してもよろしいですか？\n※削除すると元に戻せません。");
        if (!confirmDelete) return;

        setLoading(true);
        try {
            if (id) {
                await api.delete(`/api/applications/drafts/${id}/`);
            } 
            // ローカルでも削除
            localStorage.removeItem(DRAFT_KEY);
            alert('下書きを削除しました。');
            navigate('/applications');
        } catch (error) {
            console.error('下書きの削除に失敗:', error);
            alert('下書きの削除に失敗しました。もう一度お試しください。');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (onBack) onBack();
        else navigate('/applications');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'branchNameKana') {
            setFormData((prev) => ({
                ...prev,
                branchNameKana: toFullWidthKatakana(furiganaToFullWidth(value)),
            }));
            return;
        }
        if (name === 'accountNumber') {
            const digits = value.replace(/\D/g, '').slice(0, 16);
            setFormData((prev) => ({ ...prev, accountNumber: digits }));
            return;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const isConfirmed = window.confirm('振込先変更申請を送信してもよろしいですか？');

        if (!isConfirmed) {
            return;
        }
        const payload = {
            bankName: formData.bankName,
            branchName: formData.branchName,
            branchNameKana: formData.branchNameKana,
            accountNumber: formData.accountNumber,
            desiredDate: formData.desiredDate,
        };

        try {
            await api.post('/api/forms/submit_bank_change/', payload);
            localStorage.removeItem(DRAFT_KEY);
            alert(
                applicationSuccessMessage('銀行口座変更届が正常に提出されました。', [
                    '申請一覧から内容・状態を確認できます。',
                ])
            );
            navigate('/applications');
        } catch (error) {
            console.error('Submission failed:', error);
            alert('申請に失敗しました。もう一度お試しください。');
        }
    };

    const canSubmit = useMemo(
        () =>
            Boolean(
                formData.bankName?.trim() &&
                    formData.branchName?.trim() &&
                    formData.branchNameKana?.trim() &&
                    formData.accountNumber?.trim() &&
                    formData.desiredDate
            ),
        [formData.bankName, formData.branchName, formData.branchNameKana, formData.accountNumber, formData.desiredDate]
    );

    return (
        <div className="container form-container">
            <h3>振込先変更届</h3>
            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label htmlFor="bankSearch" className="form-label">
                        銀行を検索（3文字以上・ひらがな／カタカナ）
                    </label>
                    <input
                        type="text"
                        id="bankSearch"
                        name="bankSearch"
                        className="form-control"
                        value={bankQuery}
                        onChange={(e) => setBankQuery(e.target.value)}
                        autoComplete="off"
                        placeholder="例: みずほ"
                    />
                    <p className="form-suggest-hint">
                        全国の金融機関データから検索します。候補をタップして銀行名を確定してください。
                    </p>
                    {bankOptions.length > 0 && (
                        <div className="form-suggest-box" role="listbox" aria-label="銀行候補">
                            {bankOptions.map((b) => (
                                <button
                                    key={b.code}
                                    type="button"
                                    className="form-suggest-item"
                                    onClick={() => selectBank(b)}
                                >
                                    {b.normalize?.name || b.name}{' '}
                                    <span style={{ color: '#6c757d' }}>({b.code})</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="mb-3">
                    <label htmlFor="bankName" className="form-label">銀行名</label>
                    <input
                        type="text"
                        id="bankName"
                        name="bankName"
                        className={requiredFieldClass(formData.bankName)}
                        value={formData.bankName}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                <div className="mb-3">
                    <label htmlFor="branchSearch" className="form-label">
                        支店を検索（銀行確定後・3文字以上）
                    </label>
                    <input
                        type="text"
                        id="branchSearch"
                        name="branchSearch"
                        className="form-control"
                        value={branchQuery}
                        onChange={(e) => setBranchQuery(e.target.value)}
                        disabled={!selectedBankCode}
                        autoComplete="off"
                        placeholder={selectedBankCode ? '例: しぶや' : '先に銀行を候補から選択'}
                    />
                    {branchOptions.length > 0 && (
                        <div className="form-suggest-box" role="listbox" aria-label="支店候補">
                            {branchOptions.map((br, idx) => (
                                <button
                                    key={`${br.code}-${idx}`}
                                    type="button"
                                    className="form-suggest-item"
                                    onClick={() => selectBranch(br)}
                                >
                                    {br.normalize?.name || br.name}{' '}
                                    <span style={{ color: '#6c757d' }}>
                                        {br.normalize?.kana || br.kana || ''}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="mb-3">
                    <label htmlFor="branchName" className="form-label">支店名</label>
                    <input
                        type="text"
                        id="branchName"
                        name="branchName"
                        className={requiredFieldClass(formData.branchName)}
                        value={formData.branchName}
                        onChange={handleInputChange}
                        required
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="branchNameKana" className="form-label">
                        支店名（フリガナ）
                    </label>
                    <input
                        type="text"
                        id="branchNameKana"
                        name="branchNameKana"
                        className={requiredFieldClass(formData.branchNameKana)}
                        value={formData.branchNameKana}
                        onChange={handleInputChange}
                        required
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="accountNumber" className="form-label">口座番号</label>
                    <input
                        type="text"
                        id="accountNumber"
                        name="accountNumber"
                        className={requiredFieldClass(formData.accountNumber)}
                        value={formData.accountNumber}
                        onChange={handleInputChange}
                        inputMode="numeric"
                        required
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="desiredDate" className="form-label">変更希望日</label>
                    <input
                        type="date"
                        id="desiredDate"
                        name="desiredDate"
                        className={requiredFieldClass(formData.desiredDate)}
                        value={formData.desiredDate}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                <div className="button-group">
                    <button type="submit" className="btn-submit" disabled={!canSubmit}>
                        申請
                    </button>
                    <button type="button" className="btn-draft" onClick={handleSaveDraft}>
                        一時保存
                    </button>
                    {((id && isDraft) || localStorage.getItem(DRAFT_KEY)) && <button type="button" className="btn-delete-draft" onClick={handleDeleteDraft} disabled={loading}>
                        下書削除
                    </button>}
                    <button type="button" className="btn-back" onClick={handleBack}>
                        {onBack && !isFirstForm ? '前へ' : '戻る'}
                    </button>
                    {isMultiForm && onNext && (
                        <button type="button" className="btn-nav" onClick={onNext}>
                            {isLastForm ? '完了' : '次へ'}
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default BankChangeForm;
